/**
 * Color utility functions for manipulating hex colors
 */

/**
 * Converts a hex color to HSL
 * @param {string} hex - Hex color (e.g., "#ff0000" or "ff0000")
 * @returns {object} HSL object with h, s, l properties
 */
export function hexToHsl(hex) {
  // Remove # if present
  hex = hex.replace("#", "");

  // Parse the hex values
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const rHex = Math.round((r + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const gHex = Math.round((g + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const bHex = Math.round((b + m) * 255)
    .toString(16)
    .padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
}

/**
 * Makes a hex color darker by reducing lightness
 * @param {string} hex - Hex color (e.g., "#ff0000")
 * @param {number} amount - Amount to darken (0-100, default 20)
 * @returns {string} Darker hex color
 */
export function darkenHex(hex, amount = 20) {
  const hsl = hexToHsl(hex);
  const newLightness = Math.max(0, hsl.l - amount);
  return hslToHex(hsl.h, hsl.s, newLightness);
}

/**
 * Makes a hex color lighter by increasing lightness
 * @param {string} hex - Hex color (e.g., "#ff0000")
 * @param {number} amount - Amount to lighten (0-100, default 20)
 * @returns {string} Lighter hex color
 */
export function lightenHex(hex, amount = 20) {
  const hsl = hexToHsl(hex);
  const newLightness = Math.min(100, hsl.l + amount);
  return hslToHex(hsl.h, hsl.s, newLightness);
}

/**
 * Alternative method: Darken by reducing RGB values
 * @param {string} hex - Hex color (e.g., "#ff0000")
 * @param {number} factor - Darkening factor (0-1, default 0.2)
 * @returns {string} Darker hex color
 */
export function darkenHexRGB(hex, factor = 0.2) {
  // Remove # if present
  hex = hex.replace("#", "");

  // Parse the hex values
  let r = parseInt(hex.substr(0, 2), 16);
  let g = parseInt(hex.substr(2, 2), 16);
  let b = parseInt(hex.substr(4, 2), 16);

  // Darken by reducing each component
  r = Math.max(0, Math.round(r * (1 - factor)));
  g = Math.max(0, Math.round(g * (1 - factor)));
  b = Math.max(0, Math.round(b * (1 - factor)));

  // Convert back to hex
  const rHex = r.toString(16).padStart(2, "0");
  const gHex = g.toString(16).padStart(2, "0");
  const bHex = b.toString(16).padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
}

/**
 * Alternative method: Darken by subtracting from RGB values
 * @param {string} hex - Hex color (e.g., "#ff0000")
 * @param {number} amount - Amount to subtract from each RGB component (0-255, default 40)
 * @returns {string} Darker hex color
 */
export function darkenHexSubtract(hex, amount = 40) {
  // Remove # if present
  hex = hex.replace("#", "");

  // Parse the hex values
  let r = parseInt(hex.substr(0, 2), 16);
  let g = parseInt(hex.substr(2, 2), 16);
  let b = parseInt(hex.substr(4, 2), 16);

  // Darken by subtracting from each component
  r = Math.max(0, r - amount);
  g = Math.max(0, g - amount);
  b = Math.max(0, b - amount);

  // Convert back to hex
  const rHex = r.toString(16).padStart(2, "0");
  const gHex = g.toString(16).padStart(2, "0");
  const bHex = b.toString(16).padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
}

